/**
 * The typing engine — a pure state machine over a target string.
 *
 * Character-level rather than word-level (which is what the adult test uses),
 * because a beginner needs feedback on the keystroke, not on the word. Every
 * function here is pure: same snapshot in, same snapshot out. That makes the
 * learning rules testable and keeps React out of the model entirely.
 */
import type { EngineSnapshot } from '../types';

export interface KeyOptions {
  /**
   * Strict mode blocks on a wrong key: the cursor does not advance until the
   * correct one is pressed. Used for Levels 1–10, where the goal is muscle
   * memory. Turning it off from Level 11 lets fluency develop.
   */
  strict: boolean;
  /** Injected so scoring is deterministic in tests. */
  now: number;
}

export function createSnapshot(target: string): EngineSnapshot {
  const states = new Array(target.length).fill('pending') as EngineSnapshot['states'];
  if (states.length) states[0] = 'current';
  return {
    target,
    states,
    cursor: 0,
    keystrokes: 0,
    correctFirstTry: 0,
    mistakes: 0,
    keyStats: {},
    startedAt: 0,
    lastAt: 0,
    finished: false,
    everWrong: new Array(target.length).fill(false),
    lastWrong: null,
  };
}

function tally(
  stats: EngineSnapshot['keyStats'],
  char: string,
  correct: boolean
): EngineSnapshot['keyStats'] {
  const prev = stats[char] ?? { correct: 0, wrong: 0 };
  return {
    ...stats,
    [char]: correct
      ? { correct: prev.correct + 1, wrong: prev.wrong }
      : { correct: prev.correct, wrong: prev.wrong + 1 },
  };
}

/** Apply one printable character. Returns the same object when it is a no-op. */
export function applyKey(snap: EngineSnapshot, typed: string, opts: KeyOptions): EngineSnapshot {
  if (snap.finished || snap.cursor >= snap.target.length) return snap;

  const index = snap.cursor;
  const expected = snap.target[index];
  const correct = typed === expected;

  const states = snap.states.slice();
  const everWrong = snap.everWrong.slice();
  const startedAt = snap.startedAt || opts.now;

  if (correct) {
    // A position that was previously missed grades as `fixed` — the child got
    // there, but the heat map should remember the key was not automatic.
    states[index] = everWrong[index] ? 'fixed' : 'correct';
    const nextCursor = index + 1;
    if (nextCursor < states.length) states[nextCursor] = 'current';

    return {
      ...snap,
      states,
      cursor: nextCursor,
      keystrokes: snap.keystrokes + 1,
      correctFirstTry: snap.correctFirstTry + (everWrong[index] ? 0 : 1),
      keyStats: tally(snap.keyStats, expected, !everWrong[index]),
      startedAt,
      lastAt: opts.now,
      finished: nextCursor >= states.length,
      lastWrong: null,
    };
  }

  // ── wrong key ──────────────────────────────────────────────────────────
  everWrong[index] = true;
  const keyStats = tally(snap.keyStats, expected, false);
  const lastWrong = {
    index,
    expected,
    typed,
    seq: (snap.lastWrong?.seq ?? 0) + 1,
  };

  if (opts.strict) {
    // Hold position. The character is flagged so the UI can flash it red, but
    // the cursor stays put until the right key arrives.
    states[index] = 'incorrect';
    return {
      ...snap,
      states,
      keystrokes: snap.keystrokes + 1,
      mistakes: snap.mistakes + 1,
      keyStats,
      startedAt,
      lastAt: opts.now,
      everWrong,
      lastWrong,
    };
  }

  states[index] = 'incorrect';
  const nextCursor = index + 1;
  if (nextCursor < states.length) states[nextCursor] = 'current';

  return {
    ...snap,
    states,
    cursor: nextCursor,
    keystrokes: snap.keystrokes + 1,
    mistakes: snap.mistakes + 1,
    keyStats,
    startedAt,
    lastAt: opts.now,
    finished: nextCursor >= states.length,
    everWrong,
    lastWrong,
  };
}

/**
 * Backspace. In strict mode it only clears a pending red flag (there is
 * nothing to walk back to, because a wrong key never advanced the cursor).
 */
export function applyBackspace(snap: EngineSnapshot, opts: KeyOptions): EngineSnapshot {
  if (snap.finished) return snap;

  const states = snap.states.slice();

  if (opts.strict) {
    if (states[snap.cursor] !== 'incorrect') return snap;
    states[snap.cursor] = 'current';
    return { ...snap, states, lastWrong: null };
  }

  if (snap.cursor === 0) return snap;
  const target = snap.cursor - 1;
  states[snap.cursor] = 'pending';
  states[target] = 'current';
  return { ...snap, states, cursor: target, lastWrong: null };
}

/** Characters that ended up correct (including corrected ones). */
export function typedCorrectChars(snap: EngineSnapshot): number {
  let n = 0;
  for (let i = 0; i < snap.cursor; i++) {
    if (snap.states[i] === 'correct' || snap.states[i] === 'fixed') n++;
  }
  return n;
}

/** 0–1 completion, used by the progress rail. */
export function completion(snap: EngineSnapshot): number {
  return snap.target.length ? snap.cursor / snap.target.length : 0;
}

/**
 * Seconds elapsed since the first keystroke. Falls back to a floor of one
 * second so a two-character drill cannot report a nonsense 4000 WPM.
 */
export function elapsedSeconds(snap: EngineSnapshot, now: number): number {
  if (!snap.startedAt) return 0;
  const end = snap.finished ? snap.lastAt : now;
  return Math.max((end - snap.startedAt) / 1000, 1);
}
